import React, { useEffect, useMemo, useState } from "react";
import { Grid, Container } from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import { BreadCrumb, Button, Typography } from "design_system/src";
import { TImages, TVehicle } from "models/inventory/interfaces/vehicle.interface";
import { getVehicle } from "models/inventory/controllers/vehicle.controller";
import { LenderEnum, loanTypeValues, vehicleColorValues, VehicleStatusEnum } from "models/inventory/enums/vehicle.enums";
import MetaTags from "components/head/MetaTags";
import { NotFoundScreen } from "views/NotFoundScreen";
import NoAvailableVehicle from "views/noAvailableVehicle/NoAvailableVehicle";
import { getVehicleDetails } from "models/inventory/controllers/vehicle-detail.controller";
import { getVehicleReviews } from "models/inventory/controllers/vehicle-reviews.controller";
import { vehicleDetailData } from "models/inventory/interfaces/vehicle-detail.interfaces";
import { VehicleReviewSummary } from "models/inventory/interfaces/vehicle-review-summary.interfaces";
import { formatPrice } from "helpers/formatPrice";
import { prices } from "helpers/prices";
import { LoadingScreen } from "views/LoadingScreen";
import Description from "./components/Description/Description";
import RealState from "./components/Description/RealState";
import SummaryPayment from "./components/SummaryPayment/SummaryPayment";
import DetailTags from "./components/DetailTags/DetailTags";
import ReviewsSummary from "./components/Reviews/ReviewsSummary";
import Specs from "./components/Specs/Specs";
import Photos from "./components/Photos/Photos";
import Reviews from "./components/Reviews/Reviews";
import OutdoorPhotos from "./components/OutdoorPhotos/OutdoorPhotos";
import FeaturedPhotos from "./components/FeaturedPhotos/FeaturedPhotos";
import styles from "./VehicleDetail.module.scss";
import LenderSummary from "./components/SummaryPayment/LenderSummary";


const VehicleDetail = () => {
  const location = useLocation();
  const { id } = useParams();
  const [isMobile, setIsMobile] = useState<boolean | null>(null);

  const [vehicle, setvehicle] = useState<TVehicle>();
  const [loading, setLoading] = useState<boolean>(true);
  const [unavailableVehicle, setUnavailableVehicle] = useState<boolean>(false);
  const [nonExistentVehicle, setNonExistentVehicle] = useState<boolean>(false);

  const [photos, setPhotos] = useState<TImages[]>([]);

  const [vehicleDetails, setVehicleDetails] = useState<vehicleDetailData>();
  const [vehicleReview, setVehicleReview] = useState<VehicleReviewSummary>();
  const [selectedLender, setSelectedLender] = useState<LenderEnum>();

  const { weeks: selectedWeeksProps } = location?.state || {};

  const showOutdoorPhotos = useMemo<boolean>(
    () => !!(vehicle?.isNewVehicle && vehicleDetails?.images?.length) || !!(vehicle?.detailsImages?.length),
    [vehicle, vehicleDetails],
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 600px)");

    const handleChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mediaQuery.matches);

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    (async () => {
      setvehicle(undefined);

      if(!id) {
        setLoading(false);
        return;
      }

      const res = await getVehicle(id);
      if (res) {
        setvehicle(res);
        setSelectedLender(res.lenders.includes(LenderEnum.Ozon) ? LenderEnum.Ozon : res.lenders[0]);
        if (res.images) {
          setPhotos(res.images);
        }
      } else {
        setNonExistentVehicle(true);
      }
      setLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    setUnavailableVehicle(false);

    if (vehicle && vehicle.status !== VehicleStatusEnum.AVAILABLE) {
      setUnavailableVehicle(true);
    }

    if (vehicle && vehicle.status === VehicleStatusEnum.AVAILABLE) {
      getVehicleDetails(vehicle!).then((data) => {
        setVehicleDetails(data);
      });
      getVehicleReviews(vehicle!).then((data) => {
        setVehicleReview(data as VehicleReviewSummary);
      });
    }
  }, [vehicle?._id]);

  if (loading || isMobile === null) return <LoadingScreen />;

  return (
    <>
      {nonExistentVehicle && <MetaTags
        title="Compra y vende tu moto nueva o seminueva a crédito en CDMX"
        description="Financia tu moto nueva o seminueva sin enganche en CDMX. Compra y venta fácil, pagos cómodos, entrega inmediata en Ozon."
        noIndex
      />}
      {!nonExistentVehicle && <MetaTags
        title={`Compra tu ${vehicle?.brand.name} ${vehicle?.model.name} ${vehicle?.cylindersCapacity}cc año ${vehicle?.year} ${vehicle?.isNewVehicle ? "nueva" : "seminueva"} en ${vehicle?.location.city.name}`}
        description={`Financia tu moto ${vehicle?.brand.name} ${vehicle?.model.name} ${vehicle?.isNewVehicle ? "nueva" : "seminueva"} de ${vehicle?.cylindersCapacity}cc, año ${vehicle?.year}, en ${vehicle?.location.city.name}. Sin enganche, entrega rápida y pagos cómodos. `}
        noIndex={unavailableVehicle}
      />}
      {nonExistentVehicle && <NotFoundScreen/>}
      {unavailableVehicle && <NoAvailableVehicle model={vehicle?.model.name ?? ""} internalId={vehicle?.internalId ?? ""}/>}
      {!nonExistentVehicle && !unavailableVehicle &&
        <Container maxWidth="xl" className="p_t_lg">
          <Grid
            container
            justifyContent="center"
            columnSpacing={{ xs: 0, sm: 2, md: 3, lg: 3, xl: 3 }}
            rowSpacing={2}
            className="m_b_xl p_x_xxxl_desktop"
          >
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
            >
              <BreadCrumb url={location.pathname} />
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
              md={7}
              className="m_b_md"
            >
              <Photos
                photos={photos}
                isMobile={isMobile}
                showLenderImg={
                  (vehicle?.lenders ?? []).includes(LenderEnum.Atrato) ||
                  (vehicle?.lenders ?? []).includes(LenderEnum.Maxikash)
                }
              />

              {/* Description section for desktop devices */}
              {!isMobile && vehicle && <Description vehicle={vehicle} vehicleDetails={vehicleDetails}/>}
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
              md={5}
              className="m_b_md"
            >
              <div className="dso_card p_x_xl p_y_lg m_b_xl_mobile">
                <div className="m_b_sm">
                  <DetailTags vehicle={vehicle} />
                </div>

                <Typography
                  scale="heading1"
                  scaleMobile="heading2"
                  weight="600"
                  component="h1"
                  className="m_none"
                >
                  {vehicle?.brand.name ?? ""} {vehicle?.model.name ?? ""} {vehicle?.cylindersCapacity ?? ""} {vehicle?.color ? vehicleColorValues[vehicle?.color].toUpperCase() : ""}
                </Typography>

                {vehicleReview && vehicleReview?.count > 0 && <ReviewsSummary value={vehicleReview?.getGlobalRating()} amount={vehicleReview?.count}/>}

                <Typography
                  scale="large"
                  scaleMobile="large"
                  weight="400"
                  component="p"
                  className="m_none p_y_xs"
                >
                  Precio: <Typography
                  scale="large"
                  scaleMobile="large"
                  weight="600"
                  component="span"
                >
                  ${formatPrice(prices(vehicle?.marketPrice), "")}
                </Typography>
                </Typography>

                <Typography
                  scale="large"
                  scaleMobile="large"
                  weight="600"
                  component="p"
                  className="m_none p_y_xs"
                >
                  Financia tu moto con: {vehicle?.OzonLoanOnly() && <Typography
                  scale="large"
                  scaleMobile="large"
                  weight="600"
                  component="span"
                  className="text_primary_300"
                >
                  Ozon
                </Typography>}
                </Typography>

                <div className={styles.VehicleLender}>
                  {!vehicle?.OzonLoanOnly() && <div className="display_flex flex_gap_sm">
                    {vehicle?.lenders.map((type) => {
                      return (
                        <div
                          key={type}
                          onClick={() => {
                            if (type === selectedLender) return;
                            setSelectedLender(type);
                          }}
                          className={`flex_grow_3 ${styles.VehicleLenderBtn}${type === selectedLender
                            ? ` ${styles["VehicleLenderBtn--selected"]}`
                            : ""}`
                          }
                        >
                          <Typography
                            scale="medium"
                            weight="600"
                            className="text_center"
                          >
                            {type}
                          </Typography>
                        </div>
                      );
                    })}
                  </div>}

                  {selectedLender && loanTypeValues[selectedLender] !== undefined && (
                    <LenderSummary type={selectedLender} vehicle={vehicle} />
                  )}
                </div>
                {/* Week payment selection section */}
                {selectedLender === LenderEnum.Ozon && <div>
                  <Typography scale="small" weight="400" className="text_green_300 bg_green_100 p_x_md p_y_sm br_sm" component="p">5% de descuento si eres repartidor de uber o didi</Typography>
                  <SummaryPayment vehicle={vehicle} selectedWeeksProps={selectedWeeksProps}/>
                </div>}
              </div>

              {/* Description section for mobile devices */}
              {isMobile && vehicle &&<Description vehicle={vehicle} vehicleDetails={vehicleDetails}/>}
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
              md={showOutdoorPhotos ? 6 : 12}
              className="m_b_md"
            >
              <Specs vehicle={vehicle} vehicleDetails={vehicleDetails} fullWidth={showOutdoorPhotos} />
            </Grid>

            {!isMobile && showOutdoorPhotos && <Grid
              item
              xs={12}
              sm={12}
              md={6}
              className="m_b_md"
            >
              <OutdoorPhotos vehicle={vehicle} vehicleDetails={vehicleDetails} />
            </Grid>}

            <Grid
              item
              xs={12}
              className="m_b_md"
            >
              <FeaturedPhotos
                vehicle={vehicle}
                vehicleDetails={vehicleDetails}
                isMobile={isMobile}
              />
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              className="m_b_md"
            >
              <RealState vehicle={vehicle} />
              <Reviews vehicle={vehicle} vehicleReview={vehicleReview} />
            </Grid>
          </Grid>
        </Container>
      }
    </>
  );
};

export default VehicleDetail;
